/**
 * 纹理复杂度分析器
 * 使用亮度方差法计算碎片的视觉复杂度
 */
import { ComplexityLevel } from '../types';
import { SlicedPiece } from './ImageSlicer';

export interface ComplexityResult {
    pieceId: string;
    variance: number;
    level: ComplexityLevel;
}

export class ComplexityAnalyzer {
    // 全局复用的离屏 Canvas
    private analysisCanvas: HTMLCanvasElement;
    private analysisCtx: CanvasRenderingContext2D;

    // 复杂度分级阈值（百分比）
    private readonly HIGH_THRESHOLD = 0.30;   // 前 30% 为高复杂度
    private readonly LOW_THRESHOLD = 0.70;    // 后 30% 为低复杂度（即 70% 之后）

    constructor() {
        // 创建离屏 Canvas（不加入 DOM）
        this.analysisCanvas = document.createElement('canvas');
        const ctx = this.analysisCanvas.getContext('2d');
        if (!ctx) {
            throw new Error('Failed to create analysis canvas context');
        }
        this.analysisCtx = ctx;
    }

    /**
     * 分析所有碎片的复杂度
     * @param slicedPieces 切割后的碎片数组
     * @returns 按复杂度排序的结果数组
     */
    analyzeAll(slicedPieces: SlicedPiece[]): ComplexityResult[] {
        const results: ComplexityResult[] = [];

        // 计算每个碎片的亮度方差
        for (const piece of slicedPieces) {
            const pieceId = `piece_${piece.coord.u}_${piece.coord.v}`;
            const variance = this.calculateVariance(piece.texture);
            results.push({
                pieceId,
                variance,
                level: ComplexityLevel.MEDIUM  // 先设置默认值，后面统一分级
            });
        }

        // 按方差从高到低排序
        results.sort((a, b) => b.variance - a.variance);

        // 根据相对排序分配复杂度等级
        const totalCount = results.length;
        const highCount = Math.floor(totalCount * this.HIGH_THRESHOLD);
        const lowStart = Math.floor(totalCount * this.LOW_THRESHOLD);

        for (let i = 0; i < results.length; i++) {
            if (i < highCount) {
                results[i].level = ComplexityLevel.HIGH;
            } else if (i >= lowStart) {
                results[i].level = ComplexityLevel.LOW;
            } else {
                results[i].level = ComplexityLevel.MEDIUM;
            }
        }

        return results;
    }

    /**
     * 计算单个碎片纹理的亮度方差
     * @param texture 碎片纹理 Canvas
     */
    private calculateVariance(texture: HTMLCanvasElement): number {
        const width = texture.width;
        const height = texture.height;

        // 调整分析 Canvas 大小
        this.analysisCanvas.width = width;
        this.analysisCanvas.height = height;

        // 绘制纹理到分析 Canvas
        this.analysisCtx.clearRect(0, 0, width, height);
        this.analysisCtx.drawImage(texture, 0, 0);

        // 获取像素数据
        const imageData = this.analysisCtx.getImageData(0, 0, width, height);
        const data = imageData.data;

        let sum = 0;
        let sumSq = 0;
        let count = 0;

        // 遍历所有像素
        for (let i = 0; i < data.length; i += 4) {
            const r = data[i];
            const g = data[i + 1];
            const b = data[i + 2];
            const a = data[i + 3];

            // 跳过透明像素（alpha < 128）
            if (a < 128) continue;

            // 计算灰度值（使用标准权重）
            const gray = 0.299 * r + 0.587 * g + 0.114 * b;
            sum += gray;
            sumSq += gray * gray;
            count++;
        }

        // 避免除零
        if (count === 0) return 0;

        // 计算方差: Var(X) = E(X²) - E(X)²
        const mean = sum / count;
        const variance = (sumSq / count) - (mean * mean);

        return variance;
    }

    /**
     * 获取复杂度等级的数量统计
     */
    getStatistics(results: ComplexityResult[]): {
        high: number;
        medium: number;
        low: number;
    } {
        let high = 0, medium = 0, low = 0;

        for (const result of results) {
            switch (result.level) {
                case ComplexityLevel.HIGH:
                    high++;
                    break;
                case ComplexityLevel.MEDIUM:
                    medium++;
                    break;
                case ComplexityLevel.LOW:
                    low++;
                    break;
            }
        }

        return { high, medium, low };
    }

    /**
     * 清理资源
     */
    destroy(): void {
        // Canvas 会被垃圾回收，无需特殊处理
        this.analysisCanvas.width = 0;
        this.analysisCanvas.height = 0;
    }
}
